/**
 * widgetDataForms — shared custom-metric + benchmark + goal config sub-forms
 * (Story 37.5).
 *
 * The single source of truth for the report editor's THREE heaviest config
 * sub-forms. MOVED (not copied) out of `WidgetConfigPanel.tsx` so that BOTH the
 * widget config panel (editing an existing widget) AND the Phase-2 category
 * pickers (`CustomMetricsCategoryPanel` / `BenchmarksCategoryPanel` /
 * `GoalsCategoryPanel`, configure-then-add) consume one definition and cannot
 * drift apart. The epic flags these as the TOP drift risk precisely because they
 * are the heaviest — the heavier the form, the higher the cost of a copy, so a
 * single source of truth here is the build-to-sell payoff. (Phase-2 anti-drift
 * mandate / AC #4 / AC #11.)
 *
 * Consumers (both import the SAME exported symbols — no redeclaration anywhere):
 *   1. `WidgetConfigPanel.tsx` — edits an existing widget; passes the real
 *      widget + its real `onUpdate` + the client-narrowed dynamic options
 *      (`dynamicGroupedOptions` / `dynamicMetricPlatformMap` from
 *      `useAvailableMetrics(clientId)` — that narrowing layer STAYS in
 *      WidgetConfigPanel, AC #5/#9).
 *   2. `CustomMetricsCategoryPanel` / `BenchmarksCategoryPanel` /
 *      `GoalsCategoryPanel` — configure-then-add; pass a synthetic draft-widget
 *      + a draft-merging `onUpdate` adapter, and OMIT the injectable dep props so
 *      the sub-forms fall back to their static defaults (the full catalog).
 *
 * Contract — UNCHANGED from the original (do NOT refactor to `{config,onChange}`,
 * do NOT add a `mode` prop): each sub-form takes
 * `{ widget: WidgetConfig; onUpdate: (id, changes) => void }` (+ an optional
 * injectable dep with a static default) and reads/writes `widget.config`.
 * Composition over a configuration flag — both parents render the same
 * `<XConfigSection widget onUpdate />` and differ only in what they pass.
 * [react.dev "Sharing State Between Components" + reusable-component best
 * practice, verified 2026-06-01.]
 *
 * LEAF MODULE (Pre-mortem E): this file must NOT import from
 * `WidgetConfigPanel.tsx` / `WidgetPalette.tsx` / `CanvasEditor.tsx` /
 * `categoryPickerRegistry.tsx`. Its only deps are `WidgetConfig` +
 * `METRIC_PLATFORM_MAP` (lib/templateUtils), `MetricKeyCombobox` +
 * `humanizeMetricKey` (./MetricKeyCombobox), lucide icons, and React hooks — all
 * leaf deps, no import cycle.
 *
 * SECURITY (AC #12 / Red Team): the only free-text surface here is the
 * custom-metric formula, validated by `validateFormulaExpression` — a WHITELIST
 * validator (`FORMULA_SAFE_PATTERN` allow-list + dot-property-access reject +
 * balanced-paren + unknown-variable check). The formula is config text; it is
 * NEVER `eval`'d on the client — the server re-validates + evaluates it (the
 * `SYNC:` comment pairs the two regex consts with
 * `packages/db/src/schema/report-layout-types.ts`). Do NOT add
 * `eval`/`new Function`/`dangerouslySetInnerHTML`, relax the pattern, or drop the
 * dot-access reject or the `SYNC:` comment.
 *
 * Originally Story 5.28 (custom-metric) + Story 5.29 (benchmark) + Story 5.30
 * (goal); moved here verbatim by Story 37.5 (behavior byte-for-byte unchanged —
 * AC #5). The ONLY net-new logic is the three validity predicates at the bottom.
 */

import { useState, useCallback, useMemo, useRef } from "react";
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  Calculator,
  Crosshair,
  Check,
  XCircle,
} from "lucide-react";
// Story 18.4 — §10.6: the icons used by GoalConfigSection (which this story
// edits) migrate to Phosphor; the LinkSimple icon is net-new for the linked
// banner. The remaining lucide icons back CustomMetric/Benchmark sections,
// untouched here.
import {
  Flag,
  MagnifyingGlass,
  LinkSimple,
} from "@phosphor-icons/react";
import { Switch } from "@provenleap/ui";
import type { WidgetConfig } from "../../lib/templateUtils";
import { METRIC_PLATFORM_MAP } from "../../lib/templateUtils";
import { MetricKeyCombobox, humanizeMetricKey } from "./MetricKeyCombobox";
import { SelectField } from "./SelectField";

// ── Custom Metric Config Section (Story 5.28) ────────────────────────

/**
 * Inline formula validation — duplicated from @provenleap/db/report-layout-types
 * because apps/web cannot depend on @provenleap/db (rootDir constraint).
 */
// SYNC: packages/db/src/schema/report-layout-types.ts — keep FORMULA_SAFE_PATTERN,
// DOT_PROPERTY_ACCESS_PATTERN, and FORMULA_FUNCTION_NAMES in sync (Story 16.6;
// a drift-guard test asserts the two validateFormulaExpression copies agree).
const FORMULA_SAFE_PATTERN = /^[\s\w.+\-*/()%,]+$/;
const DOT_PROPERTY_ACCESS_PATTERN = /[a-zA-Z_]\.|\.[a-zA-Z_]/;
// The 4 reserved functions — allowed identifier tokens even though they are not
// declared variables (mirrors FORMULA_FUNCTION_NAMES in services/formula-evaluator.ts).
const FORMULA_FUNCTION_NAMES = new Set(["SUM", "AVG", "MAX", "MIN"]);

export function validateFormulaExpression(formula: string, variableIds: string[]): string | null {
  if (!formula || formula.trim().length === 0) return "Formula expression is required";
  if (formula.length > 500) return "Formula expression must be 500 characters or fewer";
  if (!FORMULA_SAFE_PATTERN.test(formula)) return "Formula contains invalid characters. Only variable names, numbers, operators (+, -, *, /, %), functions (SUM, AVG, MAX, MIN), commas, and parentheses are allowed";
  if (DOT_PROPERTY_ACCESS_PATTERN.test(formula)) return "Formula contains property access (dots between identifiers). Use only variable names, numbers, and operators";
  let depth = 0;
  for (const ch of formula) {
    if (ch === "(") depth++;
    if (ch === ")") depth--;
    if (depth < 0) return "Unbalanced parentheses in formula";
  }
  if (depth !== 0) return "Unbalanced parentheses in formula";
  const tokens = formula.match(/[a-zA-Z_]\w*/g) || [];
  const variableIdSet = new Set(variableIds);
  for (const token of tokens) {
    if (!variableIdSet.has(token) && !FORMULA_FUNCTION_NAMES.has(token))
      return `Unknown variable "${token}" in formula. Defined variables: ${variableIds.join(", ")}`;
  }
  return null;
}

/** Platform display labels for metric key dropdown grouping */
export const PLATFORM_LABELS: Record<string, string> = {
  "google-analytics-4": "Google Analytics 4",
  "google-search-console": "Google Search Console",
  "google-ads": "Google Ads",
  "youtube-analytics": "YouTube Analytics",
  "google-business-profile": "Google Business Profile",
  "google-pagespeed": "Google PageSpeed",
  "facebook-pages": "Facebook Pages",
  "instagram-business": "Instagram Business",
  "linkedin-pages": "LinkedIn Pages",
};

export type GroupedMetricOption = { platform: string; label: string; metrics: string[] };

/**
 * Build grouped metric options from METRIC_PLATFORM_MAP (static fallback).
 * When dynamic metrics from useAvailableMetrics are available, those are used instead
 * to show only metrics from the client's connected integrations.
 */
function getGroupedMetricOptions(): GroupedMetricOption[] {
  const groups: Record<string, string[]> = {};
  for (const [metric, platform] of Object.entries(METRIC_PLATFORM_MAP)) {
    if (!groups[platform]) groups[platform] = [];
    groups[platform].push(metric);
  }
  return Object.entries(groups).map(([platform, metrics]) => ({
    platform,
    label: PLATFORM_LABELS[platform] ?? platform,
    metrics: metrics.sort(),
  }));
}

export const GROUPED_METRIC_OPTIONS = getGroupedMetricOptions();

const OUTPUT_FORMAT_OPTIONS = [
  { value: "number", label: "Number" },
  { value: "currency", label: "Currency" },
  { value: "percentage", label: "Percentage" },
] as const;

interface CustomMetricVariable {
  id: string;
  label: string;
  metricKey: string;
}

export function CustomMetricConfigSection({
  widget,
  onUpdate,
  groupedMetricOptions = GROUPED_METRIC_OPTIONS,
}: {
  widget: WidgetConfig;
  onUpdate: (widgetId: string, changes: Partial<WidgetConfig>) => void;
  groupedMetricOptions?: GroupedMetricOption[];
}) {
  const config = widget.config as Record<string, unknown>;
  const formulaExpression = (config.formulaExpression as string) ?? "";
  const variables = (config.variables as CustomMetricVariable[]) ?? [];
  const outputFormat = (config.outputFormat as string) ?? "number";
  const decimalPlaces = (config.decimalPlaces as number) ?? 2;
  const comparisonEnabled = (config.comparisonEnabled as boolean) ?? false;
  const title = (config.title as string) ?? "";
  const currencyCode = (config.currencyCode as string) ?? "USD";

  const configRef = useRef(widget.config);
  configRef.current = widget.config;

  const handleConfigChange = useCallback(
    (key: string, value: unknown) => {
      onUpdate(widget.id, {
        config: { ...configRef.current, [key]: value },
      });
    },
    [widget.id, onUpdate]
  );

  const variableIds = useMemo(() => variables.map((v) => v.id), [variables]);
  const formulaError = useMemo(() => {
    if (!formulaExpression) return null;
    return validateFormulaExpression(formulaExpression, variableIds);
  }, [formulaExpression, variableIds]);

  const handleVariableChange = useCallback(
    (index: number, field: keyof CustomMetricVariable, value: string) => {
      const updated = [...variables];
      const variable = { ...updated[index]! };
      if (field === "label") {
        variable.label = value;
        // Auto-generate id from label (alphanumeric + underscore)
        variable.id = value.toLowerCase().replace(/[^a-z0-9_]/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");
      } else if (field === "id") {
        variable.id = value.replace(/[^a-zA-Z0-9_]/g, "");
      } else {
        variable[field] = value;
      }
      updated[index] = variable;
      handleConfigChange("variables", updated);
    },
    [variables, handleConfigChange]
  );

  const handleAddVariable = useCallback(() => {
    if (variables.length >= 10) return;
    const newVar: CustomMetricVariable = {
      id: `var_${variables.length + 1}`,
      label: `Variable ${variables.length + 1}`,
      metricKey: "",
    };
    handleConfigChange("variables", [...variables, newVar]);
  }, [variables, handleConfigChange]);

  const handleRemoveVariable = useCallback(
    (index: number) => {
      const updated = variables.filter((_, i) => i !== index);
      handleConfigChange("variables", updated);
    },
    [variables, handleConfigChange]
  );

  return (
    <div data-testid="custom-metric-config-section" className="border-t border-input/50 pt-3 space-y-3">
      <h4 className="text-[13.5px] font-medium text-foreground flex items-center gap-1.5">
        <Calculator className="h-3.5 w-3.5" />
        Custom Metric
      </h4>

      {/* Formula Input */}
      <div>
        <label className="text-[13px] text-foreground block mb-1">Formula</label>
        <textarea
          value={formulaExpression}
          onChange={(e) => handleConfigChange("formulaExpression", e.target.value)}
          maxLength={500}
          placeholder="e.g., conversions / cost * 100"
          rows={3}
          className="w-full resize-none rounded-lg border-[0.5px] border-input bg-background px-2.5 py-2 text-[13.5px] font-mono text-foreground placeholder:text-muted-foreground shadow-xs focus:outline-none focus-visible:outline-none"
          data-testid="custom-metric-formula-input"
        />
        {/* Validation indicator */}
        {formulaExpression && (
          <div className="flex items-center gap-1 mt-1" data-testid="custom-metric-formula-validation">
            {formulaError ? (
              <>
                <XCircle className="h-3.5 w-3.5 text-destructive" />
                <span className="text-xs text-destructive">{formulaError}</span>
              </>
            ) : (
              <>
                <Check className="h-3.5 w-3.5 text-green-600" />
                <span className="text-xs text-green-600">Valid formula</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Variables List */}
      <div>
        <label className="text-[13px] text-foreground block mb-1">
          Variables ({variables.length}/10)
        </label>
        <div className="space-y-2">
          {variables.map((variable, index) => (
            <div key={index} className="p-2 rounded-lg border-[0.5px] border-input bg-background space-y-1.5" data-testid={`custom-metric-variable-${index}`}>
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  value={variable.label}
                  onChange={(e) => handleVariableChange(index, "label", e.target.value)}
                  placeholder="Label"
                  maxLength={50}
                  className="h-9 flex-1 rounded-lg border-[0.5px] border-input bg-background px-2.5 text-[13.5px] shadow-xs focus:outline-none focus-visible:outline-none"
                  data-testid={`custom-metric-variable-label-${index}`}
                />
                <button
                  type="button"
                  onClick={() => handleRemoveVariable(index)}
                  className="p-1 text-muted-foreground hover:text-destructive transition-colors"
                  aria-label={`Remove variable ${variable.label}`}
                  data-testid={`custom-metric-variable-remove-${index}`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
              <input
                type="text"
                value={variable.id}
                onChange={(e) => handleVariableChange(index, "id", e.target.value)}
                placeholder="ID (used in formula)"
                maxLength={50}
                className="h-9 w-full rounded-lg border-[0.5px] border-input bg-muted/50 px-2.5 text-[13.5px] font-mono shadow-xs focus:outline-none focus-visible:outline-none"
                data-testid={`custom-metric-variable-id-${index}`}
              />
              <MetricKeyCombobox
                value={variable.metricKey}
                onChange={(metricKey) => handleVariableChange(index, "metricKey", metricKey)}
                groups={groupedMetricOptions}
                labelForKey={humanizeMetricKey}
                placeholder="Select metric..."
                testId={`custom-metric-variable-metric-${index}`}
                compact
                ariaLabel={`Metric key for variable ${variable.label || variable.id}`}
              />
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={handleAddVariable}
          disabled={variables.length >= 10}
          className="mt-2 flex items-center gap-1 text-xs text-foreground hover:text-foreground/80 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="custom-metric-add-variable"
        >
          <Plus size={12} />
          Add Variable
        </button>
      </div>

      {/* Output Format */}
      <div>
        <label className="text-[13px] text-foreground block mb-1">Output Format</label>
        <div className="flex gap-2">
          {OUTPUT_FORMAT_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border-[0.5px] text-xs cursor-pointer transition-colors ${
                outputFormat === opt.value
                  ? "border-input bg-foreground/10 text-foreground"
                  : "border-input text-muted-foreground bg-background hover:bg-accent/50"
              }`}
            >
              <input
                type="radio"
                name="custom-metric-format"
                value={opt.value}
                checked={outputFormat === opt.value}
                onChange={(e) => handleConfigChange("outputFormat", e.target.value)}
                className="sr-only"
                data-testid={`custom-metric-format-${opt.value}`}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </div>

      {/* Currency Code (only when currency) */}
      {outputFormat === "currency" && (
        <div>
          <label className="text-[13px] text-foreground block mb-1">Currency Code</label>
          <input
            type="text"
            value={currencyCode}
            onChange={(e) => handleConfigChange("currencyCode", e.target.value.toUpperCase())}
            maxLength={10}
            placeholder="USD"
            className="h-9 w-full rounded-lg border-[0.5px] border-input bg-background px-2.5 text-[13.5px] text-foreground shadow-xs focus:outline-none focus-visible:outline-none"
            data-testid="custom-metric-currency-code"
          />
        </div>
      )}

      {/* Decimal Places */}
      <div>
        <label className="text-[13px] text-foreground flex items-center justify-between mb-1">
          <span>Decimal Places</span>
          <span className="text-muted-foreground">{decimalPlaces}</span>
        </label>
        <input
          type="range"
          min={0}
          max={4}
          step={1}
          value={decimalPlaces}
          onChange={(e) => handleConfigChange("decimalPlaces", Number(e.target.value))}
          className="w-full accent-primary"
          data-testid="custom-metric-decimal-places"
        />
        <div className="flex justify-between text-[11px] text-muted-foreground">
          <span>0</span>
          <span>4</span>
        </div>
      </div>

      {/* Comparison Toggle */}
      <div className="flex items-center justify-between">
        <label className="text-[13px] text-foreground">Period Comparison</label>
        <button
          type="button"
          role="switch"
          aria-checked={comparisonEnabled}
          onClick={() => handleConfigChange("comparisonEnabled", !comparisonEnabled)}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
            comparisonEnabled ? "bg-foreground" : "bg-muted"
          }`}
          data-testid="custom-metric-comparison-toggle"
        >
          <span
            className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
              comparisonEnabled ? "translate-x-4" : "translate-x-0.5"
            }`}
          />
        </button>
      </div>

      {/* Title Input */}
      <div>
        <label className="text-[13px] text-foreground block mb-1">Title (optional)</label>
        <input
          type="text"
          value={title}
          onChange={(e) => handleConfigChange("title", e.target.value)}
          maxLength={200}
          placeholder="e.g., ROAS"
          className="h-9 w-full rounded-lg border-[0.5px] border-input bg-background px-2.5 text-[13.5px] text-foreground placeholder:text-muted-foreground shadow-xs focus:outline-none focus-visible:outline-none"
          data-testid="custom-metric-title-input"
        />
      </div>
    </div>
  );
}

// ── Benchmark Config Constants (kept in sync with packages/db/src/schema/benchmark-data.ts) ──

const BENCHMARK_INDUSTRIES = [
  "automotive", "b2b-saas", "e-commerce", "education", "finance-insurance",
  "healthcare", "home-services", "legal", "manufacturing", "real-estate",
  "restaurants-food", "retail", "technology", "travel-hospitality", "fitness-wellness",
] as const;

const BENCHMARK_METRIC_OPTIONS: { value: string; label: string }[] = [
  { value: "ctr", label: "CTR" },
  { value: "cpc", label: "CPC" },
  { value: "cpa", label: "CPA" },
  { value: "conversion_rate", label: "Conversion Rate" },
  { value: "bounce_rate", label: "Bounce Rate" },
  { value: "avg_session_duration", label: "Avg. Session Duration" },
];

function formatIndustryLabel(slug: string): string {
  return slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Goal Config Section (Story 5.30) ─────────────────────────────────

const GOAL_TYPE_OPTIONS = [
  { value: "budget", label: "Budget" },
  { value: "metric", label: "Metric Goal" },
  { value: "custom", label: "Custom" },
] as const;

const GOAL_UNIT_OPTIONS = [
  { value: "currency", label: "Currency" },
  { value: "number", label: "Number" },
  { value: "percentage", label: "Percentage" },
] as const;

const GOAL_PERIOD_OPTIONS = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
  { value: "custom", label: "Custom" },
] as const;

export function GoalConfigSection({
  widget,
  onUpdate,
  metricPlatformMap = METRIC_PLATFORM_MAP,
}: {
  widget: WidgetConfig;
  onUpdate: (widgetId: string, changes: Partial<WidgetConfig>) => void;
  metricPlatformMap?: Record<string, string>;
}) {
  const config = widget.config as Record<string, unknown>;
  const goalType = (config.goalType as string) ?? "";
  const targetValue = (config.targetValue as number) ?? 0;
  const currentValue = (config.currentValue as number) ?? 0;
  const unit = (config.unit as string) ?? "number";
  const currencyCode = (config.currencyCode as string) ?? "USD";
  const periodType = (config.periodType as string) ?? "monthly";
  const showPacing = (config.showPacing as boolean) ?? true;
  const showPercentage = (config.showPercentage as boolean) ?? true;
  const showDaysRemaining = (config.showDaysRemaining as boolean) ?? true;
  const thresholds = (config.thresholds as { warning: number; danger: number }) ?? { warning: 80, danger: 100 };
  const title = (config.title as string) ?? "";

  // Story 18.4 — a widget bound to a saved goal/budget reads its
  // goalType/target/metric/period/thresholds from the persisted row at populate
  // time; those fields are hidden here (showing editable controls that don't
  // persist anywhere would lie). The display toggles + title stay editable.
  const savedGoalId = (config.savedGoalId as string) ?? "";
  const savedBudgetId = (config.savedBudgetId as string) ?? "";
  const linkedKind: "goal" | "budget" | null = savedGoalId
    ? "goal"
    : savedBudgetId
      ? "budget"
      : null;

  const configRef = useRef(widget.config);
  configRef.current = widget.config;

  const handleConfigChange = useCallback(
    (key: string, value: unknown) => {
      onUpdate(widget.id, {
        config: { ...configRef.current, [key]: value },
      });
    },
    [widget.id, onUpdate],
  );

  // Code review (pass 1): apply MULTIPLE config keys in ONE update. Two back-to-
  // back `handleConfigChange` calls both spread the SAME render-scoped
  // `configRef.current` (it only refreshes on render), so the second call would
  // overwrite the first key with its stale value — e.g. the platform select
  // changing `platform` + clearing `metricKey` dropped the `platform` write under
  // both the WidgetConfigPanel reducer and the category-panel draft-merge.
  const handleConfigChangeMany = useCallback(
    (patch: Record<string, unknown>) => {
      onUpdate(widget.id, {
        config: { ...configRef.current, ...patch },
      });
    },
    [widget.id, onUpdate],
  );

  // Story 18.4 — Unlink in ONE update (never two sequential single-key writes;
  // the stale-configRef hazard documented above). Build a cleaned config that
  // OMITS both binding keys (object-rest delete, not a `key: undefined` spread
  // that would leave a dangling key), and restore whatever manual values the
  // config already carried so the full manual form returns populated.
  const handleUnlink = useCallback(() => {
    const current = configRef.current as Record<string, unknown>;
    const {
      savedGoalId: _g,
      savedBudgetId: _b,
      ...rest
    } = current;
    void _g;
    void _b;
    onUpdate(widget.id, { config: rest });
  }, [widget.id, onUpdate]);

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [metricSearch, setMetricSearch] = useState("");

  const formatMetricLabel = useCallback((key: string) => {
    // Split camelCase, snake_case, kebab-case into spaced Title Case.
    return key
      .replace(/[_-]+/g, " ")
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .trim();
  }, []);

  const formatPlatformLabel = useCallback((p: string) => {
    return p.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }, []);

  const selectedPlatform = (config.platform as string) ?? "";

  const groupedMetricOptions = useMemo(() => {
    const search = metricSearch.trim().toLowerCase();
    const byPlatform: Record<string, Array<{ key: string; label: string }>> = {};

    for (const [key, platform] of Object.entries(metricPlatformMap)) {
      if (selectedPlatform && platform !== selectedPlatform) continue;
      const label = formatMetricLabel(key);
      if (search && !label.toLowerCase().includes(search) && !key.toLowerCase().includes(search)) {
        continue;
      }
      if (!byPlatform[platform]) byPlatform[platform] = [];
      byPlatform[platform]!.push({ key, label });
    }

    return Object.entries(byPlatform)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([platform, metrics]) => ({
        platform,
        platformLabel: formatPlatformLabel(platform),
        metrics: metrics.sort((a, b) => a.label.localeCompare(b.label)),
      }));
  }, [metricPlatformMap, selectedPlatform, metricSearch, formatMetricLabel, formatPlatformLabel]);

  const totalMatches = useMemo(
    () => groupedMetricOptions.reduce((sum, g) => sum + g.metrics.length, 0),
    [groupedMetricOptions],
  );

  return (
    <div data-testid="goal-config-section" className="border-t border-input/50 pt-3 space-y-3">
      <h4 className="text-[13.5px] font-medium text-foreground flex items-center gap-1.5">
        <Flag size={14} />
        Goal Configuration
      </h4>

      {/* Story 18.4 — linked banner: when bound to a saved goal/budget, the
          manual data fields are hidden (the saved record is the source of
          truth, hydrated server-side); Unlink restores the full manual form. */}
      {linkedKind && (
        <div
          data-testid="goal-linked-banner"
          className="flex items-center gap-1.5 rounded-lg border-[0.5px] border-input bg-muted/40 px-2 py-1.5 text-[12.5px] text-foreground"
        >
          <LinkSimple size={14} className="shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate">
            Linked to saved {linkedKind}
          </span>
          <button
            type="button"
            data-testid="goal-unlink-btn"
            onClick={handleUnlink}
            className="shrink-0 cursor-pointer text-[12.5px] font-medium text-primary hover:underline"
          >
            Unlink
          </button>
        </div>
      )}

      {/* Title (always editable — even when linked) */}
      <div>
        <label className="text-[13px] text-foreground block mb-1">Title</label>
        <input
          type="text"
          data-testid="goal-title-input"
          value={title}
          onChange={(e) => handleConfigChange("title", e.target.value)}
          placeholder="e.g., Monthly Budget: Google Ads"
          maxLength={200}
          className="h-9 w-full rounded-lg border-[0.5px] border-input bg-background px-2.5 text-[13.5px] text-foreground shadow-xs focus:outline-none focus-visible:outline-none"
        />
      </div>

      {/* Manual data fields — HIDDEN while linked (server hydration owns them). */}
      {!linkedKind && (
        <>
      {/* Goal Type selector */}
      <div>
        <label className="text-[13px] text-foreground block mb-1">Goal Type</label>
        <div className="flex gap-1" data-testid="goal-type-selector">
          {GOAL_TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => handleConfigChange("goalType", opt.value)}
              className={`flex-1 cursor-pointer px-2 py-1.5 text-xs font-medium rounded-lg border-[0.5px] transition-colors ${
                goalType === opt.value
                  ? "border-input bg-foreground/10 text-foreground"
                  : "border-input text-muted-foreground bg-background hover:bg-accent/50"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Target Value */}
      <div>
        <label className="text-[13px] text-foreground block mb-1">Target Value</label>
        <input
          type="number"
          data-testid="goal-target-input"
          value={targetValue || ""}
          onChange={(e) => handleConfigChange("targetValue", Number(e.target.value))}
          placeholder="e.g., 5000"
          min={0}
          step="any"
          className="h-9 w-full rounded-lg border-[0.5px] border-input bg-background px-2.5 text-[13.5px] text-foreground shadow-xs focus:outline-none focus-visible:outline-none"
        />
      </div>

      {/* Current Value (for budget/custom) */}
      {goalType !== "metric" && (
        <div>
          <label className="text-[13px] text-foreground block mb-1">Current Value (manual)</label>
          <input
            type="number"
            data-testid="goal-current-input"
            value={currentValue || ""}
            onChange={(e) => handleConfigChange("currentValue", Number(e.target.value))}
            placeholder="e.g., 3200"
            min={0}
            step="any"
            className="h-9 w-full rounded-lg border-[0.5px] border-input bg-background px-2.5 text-[13.5px] text-foreground shadow-xs focus:outline-none focus-visible:outline-none"
          />
        </div>
      )}

      {/* Platform filter + Metric Key dropdown (for metric goals) */}
      {goalType === "metric" && (
        <>
          <div>
            <label className="text-[13px] text-foreground block mb-1">Platform</label>
            <SelectField
              ariaLabel="Platform"
              testId="goal-platform-select"
              value={selectedPlatform}
              onChange={(v) => {
                // Atomic: set platform + clear metricKey in ONE update so the
                // second write can't clobber the first via a stale configRef.
                handleConfigChangeMany({ platform: v, metricKey: "" });
                setMetricSearch("");
              }}
              triggerClassName="h-9 w-full rounded-lg border-[0.5px] shadow-xs text-[13.5px]"
              options={[
                { value: "", label: "All platforms" },
                ...Array.from(new Set(Object.values(metricPlatformMap))).sort().map((p) => ({
                  value: p,
                  label: formatPlatformLabel(p),
                })),
              ]}
            />
          </div>
          <div>
            <label className="text-[13px] text-foreground block mb-1">Metric</label>
            <div className="relative mb-1">
              <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                data-testid="goal-metric-search"
                value={metricSearch}
                onChange={(e) => setMetricSearch(e.target.value)}
                placeholder="Search metrics (e.g., revenue, clicks)"
                className="h-9 w-full rounded-lg border-[0.5px] border-input bg-background pl-7 pr-2.5 text-[13.5px] text-foreground shadow-xs focus:outline-none focus-visible:outline-none"
              />
            </div>
            <select
              data-testid="goal-metric-key-input"
              value={(config.metricKey as string) ?? ""}
              onChange={(e) => handleConfigChange("metricKey", e.target.value)}
              size={Math.min(10, Math.max(4, totalMatches + groupedMetricOptions.length))}
              className="w-full px-2.5 py-1.5 text-[13.5px] rounded-lg border-[0.5px] border-input bg-background text-foreground shadow-xs focus:outline-none focus-visible:outline-none"
            >
              {totalMatches === 0 ? (
                <option value="" disabled>
                  {metricSearch ? `No metrics match "${metricSearch}"` : "No metrics available"}
                </option>
              ) : (
                groupedMetricOptions.map((group) => (
                  <optgroup key={group.platform} label={group.platformLabel}>
                    {group.metrics.map(({ key, label }) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </optgroup>
                ))
              )}
            </select>
            <div className="mt-1 text-[11px] text-muted-foreground" data-testid="goal-metric-result-count">
              {totalMatches} {totalMatches === 1 ? "metric" : "metrics"}
              {metricSearch ? ` matching "${metricSearch}"` : ""}
              {selectedPlatform ? ` in ${formatPlatformLabel(selectedPlatform)}` : ""}
            </div>
          </div>
        </>
      )}

      {/* Unit selector */}
      <div>
        <label className="text-[13px] text-foreground block mb-1">Unit</label>
        <SelectField
          ariaLabel="Unit"
          testId="goal-unit-select"
          value={unit}
          onChange={(v) => handleConfigChange("unit", v)}
          triggerClassName="h-9 w-full rounded-lg border-[0.5px] shadow-xs text-[13.5px]"
          options={GOAL_UNIT_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
        />
      </div>

      {/* Currency code (when unit is currency) */}
      {unit === "currency" && (
        <div>
          <label className="text-[13px] text-foreground block mb-1">Currency</label>
          <input
            type="text"
            data-testid="goal-currency-input"
            value={currencyCode}
            onChange={(e) => handleConfigChange("currencyCode", e.target.value.toUpperCase())}
            placeholder="USD"
            maxLength={10}
            className="h-9 w-full rounded-lg border-[0.5px] border-input bg-background px-2.5 text-[13.5px] text-foreground shadow-xs focus:outline-none focus-visible:outline-none"
          />
        </div>
      )}

      {/* Period type */}
      <div>
        <label className="text-[13px] text-foreground block mb-1">Period</label>
        <SelectField
          ariaLabel="Period"
          testId="goal-period-select"
          value={periodType}
          onChange={(v) => handleConfigChange("periodType", v)}
          triggerClassName="h-9 w-full rounded-lg border-[0.5px] shadow-xs text-[13.5px]"
          options={GOAL_PERIOD_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
        />
      </div>
        </>
      )}

      {/* Display toggles (always editable — even when linked) */}
      <div className="space-y-2">
        <label className="text-[13px] text-foreground block">Display Options</label>
        {[
          { key: "showPacing", label: "Show pacing indicator", value: showPacing },
          { key: "showPercentage", label: "Show percentage", value: showPercentage },
          { key: "showDaysRemaining", label: "Show days remaining", value: showDaysRemaining },
        ].map((toggle) => (
          <label key={toggle.key} className="flex items-center gap-2 text-[13px] text-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={toggle.value}
              onChange={(e) => handleConfigChange(toggle.key, e.target.checked)}
              className="rounded border-border"
            />
            {toggle.label}
          </label>
        ))}
      </div>

      {/* Advanced: Thresholds (collapsible) — HIDDEN while linked (the saved
          record's thresholds are the source of truth, hydrated server-side). */}
      {!linkedKind && (
      <div>
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex cursor-pointer items-center gap-1 text-[13px] font-medium text-muted-foreground hover:text-foreground transition-colors"
          data-testid="goal-advanced-toggle"
        >
          {showAdvanced ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          Advanced Thresholds
        </button>
        {showAdvanced && (
          <div className="mt-2 space-y-2 pl-4">
            <div>
              <label className="text-[13px] text-foreground block mb-1">Warning % ({thresholds.warning})</label>
              <input
                type="number"
                data-testid="goal-threshold-warning"
                value={thresholds.warning}
                onChange={(e) => handleConfigChange("thresholds", { ...thresholds, warning: Number(e.target.value) })}
                min={0}
                max={200}
                className="h-9 w-full rounded-lg border-[0.5px] border-input bg-background px-2.5 text-[13.5px] text-foreground shadow-xs focus:outline-none focus-visible:outline-none"
              />
            </div>
            <div>
              <label className="text-[13px] text-foreground block mb-1">Danger % ({thresholds.danger})</label>
              <input
                type="number"
                data-testid="goal-threshold-danger"
                value={thresholds.danger}
                onChange={(e) => handleConfigChange("thresholds", { ...thresholds, danger: Number(e.target.value) })}
                min={0}
                max={200}
                className="h-9 w-full rounded-lg border-[0.5px] border-input bg-background px-2.5 text-[13.5px] text-foreground shadow-xs focus:outline-none focus-visible:outline-none"
              />
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

export function BenchmarkConfigSection({
  widget,
  onUpdate,
}: {
  widget: WidgetConfig;
  onUpdate: (widgetId: string, changes: Partial<WidgetConfig>) => void;
}) {
  const config = widget.config as Record<string, unknown>;
  const industry = (config.industry as string) ?? "";
  const metrics = (config.metrics as string[]) ?? [];
  const showPercentileRange = (config.showPercentileRange as boolean) ?? true;
  const showBadge = (config.showBadge as boolean) ?? true;
  const title = (config.title as string) ?? "";

  const configRef = useRef(widget.config);
  configRef.current = widget.config;

  const handleConfigChange = useCallback(
    (key: string, value: unknown) => {
      onUpdate(widget.id, {
        config: { ...configRef.current, [key]: value },
      });
    },
    [widget.id, onUpdate],
  );

  const handleMetricToggle = useCallback(
    (metric: string) => {
      const current = ((configRef.current as Record<string, unknown>).metrics as string[]) ?? [];
      const next = current.includes(metric)
        ? current.filter((m) => m !== metric)
        : [...current, metric];
      handleConfigChange("metrics", next);
    },
    [handleConfigChange],
  );

  return (
    <div data-testid="benchmark-config-section" className="border-t border-input/50 pt-3 space-y-3">
      <h4 className="text-[13.5px] font-medium text-foreground flex items-center gap-1.5">
        <Crosshair className="h-3.5 w-3.5" />
        Benchmark
      </h4>

      {/* Industry selector */}
      <div>
        <label className="text-[13px] text-foreground block mb-1">Industry</label>
        <SelectField
          ariaLabel="Industry"
          testId="benchmark-industry-select"
          value={industry}
          onChange={(v) => handleConfigChange("industry", v)}
          triggerClassName="h-9 w-full rounded-lg border-[0.5px] shadow-xs text-[13.5px]"
          placeholder="Select industry…"
          options={BENCHMARK_INDUSTRIES.map((ind) => ({ value: ind, label: formatIndustryLabel(ind) }))}
        />
      </div>

      {/* Metrics multi-select (checkbox list) */}
      <div>
        <label className="text-[13px] text-foreground block mb-1">
          Metrics ({metrics.length}/8)
        </label>
        <div className="space-y-1" data-testid="benchmark-metrics-list">
          {BENCHMARK_METRIC_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-2 text-[13px] text-foreground cursor-pointer"
            >
              <input
                type="checkbox"
                checked={metrics.includes(opt.value)}
                onChange={() => handleMetricToggle(opt.value)}
                disabled={!metrics.includes(opt.value) && metrics.length >= 8}
                className="rounded border-border"
              />
              {opt.label}
            </label>
          ))}
        </div>
      </div>

      {/* Toggle: Show percentile range */}
      <div className="flex items-center justify-between">
        <label className="text-[13px] text-foreground">Show P25–P75 Range</label>
        <input
          type="checkbox"
          data-testid="benchmark-percentile-toggle"
          checked={showPercentileRange}
          onChange={(e) => handleConfigChange("showPercentileRange", e.target.checked)}
          className="rounded border-border"
        />
      </div>

      {/* Toggle: Show badge */}
      <div className="flex items-center justify-between">
        <label className="text-[13px] text-foreground">Show Performance Badge</label>
        <input
          type="checkbox"
          data-testid="benchmark-badge-toggle"
          checked={showBadge}
          onChange={(e) => handleConfigChange("showBadge", e.target.checked)}
          className="rounded border-border"
        />
      </div>

      {/* Optional title */}
      <div>
        <label className="text-[13px] text-foreground block mb-1">
          Title <span className="text-muted-foreground">(optional)</span>
        </label>
        <input
          type="text"
          data-testid="benchmark-title-input"
          value={title}
          onChange={(e) => handleConfigChange("title", e.target.value)}
          maxLength={200}
          placeholder="e.g., Industry Benchmark"
          className="h-9 w-full rounded-lg border-[0.5px] border-input bg-background px-2.5 text-[13.5px] text-foreground placeholder:text-muted-foreground shadow-xs focus:outline-none focus-visible:outline-none"
        />
      </div>
    </div>
  );
}

// ── KPI Config Section (Story 8.11) ──────────────────────────────────
//
// The KPI widget gains ONE opt-in toggle: "Show industry benchmark"
// (`config.showBenchmark`, default off). When on, the server populate path
// attaches an inline benchmark indicator to the KPI card IFF the metric is
// benchmarkable, the platform is benchmarkable, the client's industry
// normalizes, a benchmark row exists, AND the tenant is Starter+. The toggle
// just records intent — the gating + the Free-plan CTA live in KpiWidget /
// the server. §10 editor primitive: label-left / Switch-right, report navy.

export function KpiConfigSection({
  widget,
  onUpdate,
}: {
  widget: WidgetConfig;
  onUpdate: (widgetId: string, changes: Partial<WidgetConfig>) => void;
}) {
  const config = widget.config as Record<string, unknown>;
  const showBenchmark = (config.showBenchmark as boolean) ?? false;

  const configRef = useRef(widget.config);
  configRef.current = widget.config;

  const handleConfigChange = useCallback(
    (key: string, value: unknown) => {
      onUpdate(widget.id, {
        config: { ...configRef.current, [key]: value },
      });
    },
    [widget.id, onUpdate],
  );

  return (
    <div data-testid="kpi-config-section" className="border-t border-input/50 pt-3 space-y-3">
      <div className="grid grid-cols-[1fr_auto] items-center gap-2">
        <span className="min-w-0 text-[13px] text-foreground">Show industry benchmark</span>
        <Switch
          aria-label="Show industry benchmark"
          data-testid="kpi-show-benchmark-toggle"
          // Override the theme's primary (red) with the report's navy — matches
          // the KPI Trend-line / account-picker Switch precedent.
          className="data-[state=checked]:bg-[#1e2a44]"
          checked={showBenchmark}
          onCheckedChange={(on) => handleConfigChange("showBenchmark", on)}
        />
      </div>
      <p className="text-[11px] text-muted-foreground">
        Compares this metric to its industry median (Google Ads, Meta Ads &
        GA4 metrics; requires a paid plan and a recognized client industry).
      </p>
    </div>
  );
}

// ── Per-type "valid for add" predicates (the ONLY net-new logic — Story 37.5) ──
//
// The configure-then-add category panels gate their Add button on these so a
// panel can NEVER drop a bare-placeholder/unconfigured widget (AC #6/#7). Each
// is exported so the panel AND its tests share ONE rule (no drift). The bar is
// "usefully configured", not "literally renders nothing" — the canvas renderers
// always draw a labeled placeholder in edit mode, so these gate against adding a
// widget that would show only that placeholder.

/**
 * Custom-metric is addable once the formula is present AND passes the SAME
 * `validateFormulaExpression` the live badge uses (so the gate and the badge
 * can't drift — AC #4/#11). A valid constant formula (zero variables, e.g.
 * `100 / 2`) is intentionally addable; the renderer's stricter edit-badge is a
 * separate, non-blocking concern. The trim pre-check is defensive/readable —
 * `validateFormulaExpression` already rejects an empty string.
 *
 * Code review (pass 1): two hardenings beyond the live validator —
 *   (1) Array.isArray guard on `variables` so the predicate can't throw if it's
 *       ever called on externally-loaded/corrupt config (it is exported for reuse);
 *   (2) require at least one alphanumeric token. validateFormulaExpression is a
 *       SYNC'd whitelist that accepts operator-only or paren-only strings, and a
 *       bare dot, as "valid"; those are non-evaluable and would add a meaningless
 *       widget (violates AC #7 "never blank"). The gate rejects them HERE without
 *       touching the byte-for-byte db-paired validator (AC #12). A constant like
 *       100 / 2 still passes (it has alphanumeric tokens).
 */
export function isCustomMetricDraftValid(config: Record<string, unknown>): boolean {
  const f = ((config.formulaExpression as string) ?? "").trim();
  if (!f) return false;
  if (!/[a-zA-Z0-9]/.test(f)) return false;
  const vars = Array.isArray(config.variables) ? (config.variables as { id: string }[]) : [];
  const ids = vars.map((v) => v.id);
  return validateFormulaExpression(f, ids) === null;
}

/**
 * Benchmark is addable once an industry is chosen AND at least one metric is
 * selected — without both, the widget has nothing to compare (only a
 * placeholder). Code review (pass 1): `Array.isArray` guard so a non-array
 * `metrics` (corrupt/external config) can't spuriously pass via `.length`.
 */
export function isBenchmarkDraftValid(config: Record<string, unknown>): boolean {
  const ind = ((config.industry as string) ?? "").trim();
  const m = Array.isArray(config.metrics) ? (config.metrics as string[]) : [];
  return ind.length > 0 && m.length >= 1;
}

/**
 * Goal is addable once a goal type is chosen AND a positive target is set;
 * additionally, a `metric` goal needs a `metricKey` (a metric goal with no
 * metric can't pace). The canvas default seeds `goalType: ""` + `targetValue: 0`,
 * so the gate starts disabled — the user MUST pick a type + target.
 */
export function isGoalDraftValid(config: Record<string, unknown>): boolean {
  const t = ((config.goalType as string) ?? "").trim();
  const target = (config.targetValue as number) ?? 0;
  if (!t || !(target > 0)) return false;
  if (t === "metric") return ((config.metricKey as string) ?? "").trim().length > 0;
  return true;
}
